/**
 * NavTray — the single unified navigation surface (nav-shell R1, Increment 1).
 *
 * Replaces BOTH the DesktopTopbar nav buttons (Cooks / Groups / Feed) AND the
 * UserMenu account dropdown/sheet with one tray that holds every destination in
 * two labelled sections:
 *
 *   Cook    — Library · Feed · Cooks · Groups · Friends
 *   Account — Profile · Settings · Export · Custom logic · Syntax help ·
 *             Send feedback · (Admin) · Sign out (pinned to the bottom)
 *
 * An identity header (avatar initials + first name + email) sits at the top.
 *
 * Three presentations, one component (Rule 7) — the mount is EXACTLY ONE; the
 * presentation is a rendering mode of that single instance, never a second mount:
 *   - Desktop (≥1024px), browse surface (`collapsed=false`, i.e. no recipe open):
 *     docked open as a persistent left column (~224px, hairline right border).
 *     No summon control — it is always visible; the content area sits to its
 *     right. `open` is ignored for visibility here (CSS keeps the panel shown).
 *   - Desktop (≥1024px), recipe open (`collapsed=true`): the tray collapses to a
 *     ~48px icon-only mini-rail pinned at the left edge — avatar, one glyph per
 *     Cook destination (active section carries the accent-soft fill + accent icon
 *     + 2px accent left-marker), and an expand `»` control. Labels surface on
 *     hover as a tooltip. The section icons NAVIGATE directly (the same action
 *     their full-tray rows run) so nav stays a glance away and one click deep;
 *     the identity avatar and the `»` expand open the FULL labelled tray as an
 *     overlay over the content (scrim behind, hairline right edge — the same
 *     summoned mechanism as mobile, just triggered on desktop). Selecting a row
 *     in that overlay navigates and closes it; the Account section + Sign out
 *     live there. §6.0a stays satisfied: the full labelled tray is one click
 *     away via the avatar / expand.
 *   - Mobile / tablet (<1024px): a full-screen overlay summoned by ONE menu
 *     control in the top bar (the hamburger, `data-testid="user-menu-trigger"`)
 *     and dismissed by the top-right ✕, the scrim, or Escape. The panel scrolls
 *     if its content exceeds the viewport; Sign out stays reachable at the
 *     bottom. `collapsed` has no effect here (CSS ignores it below 1024px) — the
 *     mini-rail is a desktop-only mode.
 *
 * Correctness-critical (P0 "two avatars, one fix" history — PRs #165/#170/#173):
 * this component is mounted EXACTLY ONCE at the route level (routes/app.tsx),
 * outside the Workspace's `display:none` subtree, so it is reachable from both
 * the Library-landing top bar and the Workspace-internal top bar. `onSignOut`
 * and every row target mirror the retired UserMenu verbatim.
 *
 * Design refs:
 *   design.md §2   — one accent (reserved to the active-row marker + text),
 *                    hairlines over shadows, quiet 200ms motion, no decoration.
 *   design.md §6.0a — a labelled primary-nav entry on EVERY breakpoint; the
 *                    30-second discoverability acceptance.
 *   design.md §2.8 — icons unambiguous AND labelled.
 *   branding.md §5, §5.2.1 — no banned words, no exclamations.
 *   CLAUDE.md Rule 7 — mobile + tablet + desktop from the first commit.
 *   CLAUDE.md Rule 8 — Sign out surfaces its failure via the caller's handler.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useIsAdmin } from "../user-menu/useIsAdmin.js";
import "./NavTray.css";

// ── Active-row key ─────────────────────────────────────────────────────────────

/** The destinations that can read as "current" from the URL. Settings, Export,
 *  Custom logic detail, Help, Feedback are surfaces without a stable landing
 *  path we highlight (Settings/Export/Feedback are modals/panels), so they are
 *  not part of this union — only path-routed destinations are. */
export type NavTrayActiveKey =
  "library" | "feed" | "discover" | "cooks" | "groups" | "profile" | "help" | "custom-logic" | null;

/**
 * Derive the active destination key from the current pathname. Best-effort:
 * unmatched paths return null (no row is marked current). Kept pure + exported
 * so the tray's own unit tests can assert the mapping without a router.
 */
export function deriveNavTrayActiveKey(pathname: string): NavTrayActiveKey {
  if (pathname.startsWith("/app/feed")) return "feed";
  if (pathname.startsWith("/app/discover")) return "discover";
  if (pathname.startsWith("/app/ledger")) return "cooks";
  if (pathname.startsWith("/app/g")) return "groups";
  if (pathname.startsWith("/app/help")) return "help";
  if (pathname.startsWith("/app/settings/custom-logic")) return "custom-logic";
  if (pathname.startsWith("/u/")) return "profile";
  // The Library row is active on the full library page (/app/library) and on the
  // recipe surface (/app/r/…), which lives inside the library area. The bare
  // dashboard (/app) is HOME (the wordmark), not the Library destination, so it
  // marks no nav row current (dashboard/library split, 2026-07-11).
  if (pathname === "/app/library" || pathname.startsWith("/app/library/")) return "library";
  if (pathname.startsWith("/app/r")) return "library";
  return null;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface NavTrayProps {
  /**
   * Mobile/tablet overlay open-state. Controlled by the route shell (the top-bar
   * hamburger toggles it). Ignored for the docked desktop column, whose
   * visibility is CSS-driven at ≥1024px.
   */
  open: boolean;
  /** Fires on every open-state transition (hamburger, ✕, scrim, Escape, row). */
  onOpenChange: (open: boolean) => void;

  /**
   * Desktop mini-rail mode. When true AND the viewport is ≥1024px, the docked
   * tray collapses to a ~48px icon-only rail (recipe-open density); the full
   * labelled tray becomes an overlay summoned by any rail control or `open`.
   * Wired to `currentRecipeUuid !== null` by the route shell. Ignored below
   * 1024px (the mobile summoned overlay is unchanged). Defaults to false.
   */
  collapsed?: boolean;

  /** First name (or @handle / email local-part) shown in the identity header. */
  firstName: string;
  /** Email shown under the name in the identity header. */
  email: string;
  /** Two-character initials for the identity avatar (e.g. "EK"). */
  avatarInitials: string;

  // ── Cook section ──
  onLibrary: () => void;
  onFeed: () => void;
  onDiscover: () => void;
  onCooks: () => void;
  onGroups: () => void;
  onFriends: () => void;

  // ── Account section ──
  onProfile: () => void;
  onSettings: () => void;
  onExports: () => void;
  onCustomLogic: () => void;
  onHelp: () => void;
  onFeedback: () => void;
  onSignOut: () => void;
  /** Admin panel. When omitted (or the session is non-admin) the row is hidden. */
  onAdmin?: () => void;

  /**
   * Override the active-row key. When omitted the tray derives it from
   * `window.location.pathname` at render (the route shell passes the derived key
   * so it stays in sync with client navigation).
   */
  activeKey?: NavTrayActiveKey;
}

// ── Icons (20×20, 1.5px stroke, currentColor — internal, not user-visible) ─────

function IconLibrary() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="3.5" y="3" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="3" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <path d="M14.5 4.4l2.2 13.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconFeed() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="3" y="3.5" width="14" height="4" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <rect x="3" y="10" width="14" height="4" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <line
        x1="3"
        y1="16.5"
        x2="12"
        y2="16.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Discover — a compass rose (explore / find). Distinct from Feed (stacked rows)
// and Library (spines). Internal glyph, always paired with the "Discover" label.
function IconDiscover() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M13.2 6.8l-1.6 4.8-4.8 1.6 1.6-4.8 4.8-1.6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCooks() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="4" cy="5" r="1.25" fill="currentColor" />
      <circle cx="4" cy="10" r="1.25" fill="currentColor" />
      <circle cx="4" cy="15" r="1.25" fill="currentColor" />
      <line
        x1="7.5"
        y1="5"
        x2="16.5"
        y2="5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7.5"
        y1="10"
        x2="16.5"
        y2="10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7.5"
        y1="15"
        x2="16.5"
        y2="15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconGroups() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7" cy="7.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.5 16c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="13.5" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M13 12c2.2 0 4 1.6 4 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Friends — a SINGLE person with a small "+" at the upper-right. Distinct from
// Profile (no plus, no enclosing ring) AND from Groups (which is two people).
function IconFriends() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="8.5" cy="7.5" r="2.75" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 16.5c0-2.8 2.24-4.6 5-4.6s5 1.8 5 4.6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M15 3.25v3.5M13.25 5h3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Profile — a single head + shoulders enclosed in an avatar ring. Reads as "your
// identity", echoing the identity-header avatar. Distinct from Friends (which
// carries a "+" and no ring).
function IconPerson() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5.9 15.4c0.5-2.1 2.2-3.4 4.1-3.4s3.6 1.3 4.1 3.4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Settings — a GEAR: a toothed ring with an inner hole. design.md §2 principle
// 8(a) bans the circle-plus-radial-spokes geometry outright (it reads as a sun,
// which is why the 2026-04-27 QA found users treating Settings as a theme
// toggle); §6.2 specifies the gear. The teeth are a single closed path so the
// silhouette reads as a cog, never as rays radiating off a disc.
function IconGear() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M8.6 2.6h2.8l.32 1.86a6.1 6.1 0 0 1 1.44.84l1.77-.66 1.4 2.42-1.45 1.2a6.15 6.15 0 0 1 0 1.68l1.45 1.2-1.4 2.42-1.77-.66c-.44.35-.92.63-1.44.84L11.4 17.4H8.6l-.32-1.86a6.1 6.1 0 0 1-1.44-.84l-1.77.66-1.4-2.42 1.45-1.2a6.15 6.15 0 0 1 0-1.68l-1.45-1.2 1.4-2.42 1.77.66c.44-.35.92-.63 1.44-.84L8.6 2.6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2.35" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconExport() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 3v10M6 9l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconCode() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <polyline
        points="7,6 3,10 7,14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="13,6 17,10 13,14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBook() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 3h7a2 2 0 0 1 2 2v11a2 2 0 0 0-2-2H4V3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13 16a2 2 0 0 1 2-2h1V5a2 2 0 0 0-2-2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconFeedback() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 4h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6l-3 3V5a1 1 0 0 1 1-1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconAdmin() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 2L4 5v5c0 3.5 2.5 6.5 6 7.5 3.5-1 6-4 6-7.5V5l-6-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSignOut() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M13 10h5M16 8l2 2-2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 3H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M4 4l10 10M14 4L4 14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Expand — a double chevron (»), the mini-rail's "open the full tray" control.
function IconExpand() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M4 4l5 5-5 5M9 4l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

interface RowProps {
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick: () => void;
  testId: string;
  destructive?: boolean;
}

function Row({ label, icon, active = false, onClick, testId, destructive = false }: RowProps) {
  const className = [
    "proof-nav-tray__row",
    active ? "proof-nav-tray__row--active" : "",
    destructive ? "proof-nav-tray__row--signout" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      data-testid={testId}
      {...(active ? { "aria-current": "page" as const } : {})}
    >
      <span className="proof-nav-tray__row-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="proof-nav-tray__row-label">{label}</span>
    </button>
  );
}

// ── Rail button (desktop mini-rail, collapsed mode) ────────────────────────────

interface RailButtonProps {
  label: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick: () => void;
  testId: string;
}

/**
 * A single icon-only destination on the collapsed mini-rail. It NAVIGATES
 * directly — the same action its full-tray row runs — so nav stays a glance
 * away and one click deep (the avatar + the `»` expand are the two controls that
 * open the full labelled overlay; the section icons do not). The destination
 * name is the accessible label and the hover tooltip; the active section adds
 * the accent-soft fill + accent icon + 2px accent left-marker (the same
 * `.row--active` idiom, design.md §2).
 */
function RailButton({ label, icon, active = false, onClick, testId }: RailButtonProps) {
  const className = ["proof-nav-tray__rail-btn", active ? "proof-nav-tray__rail-btn--active" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      data-testid={testId}
      aria-label={label}
      {...(active ? { "aria-current": "page" as const } : {})}
    >
      <span className="proof-nav-tray__rail-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="proof-nav-tray__rail-tip" role="tooltip" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NavTray({
  open,
  onOpenChange,
  firstName,
  email,
  avatarInitials,
  onLibrary,
  onFeed,
  onDiscover,
  onCooks,
  onGroups,
  onFriends,
  onProfile,
  onSettings,
  onExports,
  onCustomLogic,
  onHelp,
  onFeedback,
  onSignOut,
  onAdmin,
  activeKey,
  collapsed = false,
}: NavTrayProps) {
  const isAdmin = useIsAdmin();

  const resolvedActive: NavTrayActiveKey =
    activeKey ??
    (typeof window !== "undefined" ? deriveNavTrayActiveKey(window.location.pathname) : null);

  // Close on Escape while the mobile overlay is open. On desktop the tray is
  // docked (always open) and Escape is a no-op — the guard on `open` keeps the
  // listener off in that state.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Body scroll-lock while the mobile overlay is open. The docked desktop column
  // never sets `open` on its own (the route shell only toggles it below 1024px),
  // so this stays inert on desktop.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ── Bottom scroll cue (design.md §6.0a) ────────────────────────────────────
  // True while the list has content past its bottom edge. Drives the fade
  // below the sections so the Account rows that fall under the fold at 375x667
  // are visibly "more", not "the end". Re-measured on scroll, on open, and on
  // any resize of the region (rotation, keyboard, admin row appearing).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const syncScrollCue = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 1px slack absorbs sub-pixel rounding at the end of the scroll range.
    setHasMoreBelow(el.scrollHeight - el.clientHeight - el.scrollTop > 1);
  }, []);

  useEffect(() => {
    syncScrollCue();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => syncScrollCue());
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, isAdmin, syncScrollCue]);

  // Run a row's navigation/action, then close the mobile overlay. The actions
  // are client-navigations or route-level surface toggles that never depend on
  // the tray staying mounted, so running-then-closing is race-free (unlike the
  // retired Vaul Sheet, this overlay is a plain fixed element — no portal
  // teardown mid-click). On the docked desktop column onOpenChange(false) is a
  // harmless no-op (the state is already false there).
  function handleRow(action: () => void) {
    action();
    onOpenChange(false);
  }

  // Open the full labelled tray overlay. Only the two rail chrome controls (the
  // identity avatar and the `»` expand) call this — the rail's section icons
  // navigate directly (via handleRow) so nav stays a glance away and one click
  // deep; the Account section + Sign out live in this overlay.
  function handleExpand() {
    onOpenChange(true);
  }

  const asideClassName = [
    "proof-nav-tray",
    open ? "proof-nav-tray--open" : "",
    collapsed ? "proof-nav-tray--rail" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside className={asideClassName} data-testid="nav-tray">
      {/* Mini-rail — desktop recipe-open only (CSS-shown at ≥1024px + --rail;
          hidden on every other surface). One glyph per Cook destination (each
          NAVIGATES directly, mirroring its full-tray row) + the avatar and the
          `»` expand at the ends (the two controls that open the full labelled
          overlay carrying the Account section + Sign out). Rendered only in
          collapsed mode so browse desktop / mobile keep a clean DOM. */}
      {collapsed && (
        <div className="proof-nav-tray__rail" data-testid="nav-tray-rail">
          <button
            type="button"
            className="proof-nav-tray__rail-avatar"
            onClick={handleExpand}
            data-testid="nav-tray-rail-avatar"
            aria-label="Open navigation"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <span aria-hidden="true">{avatarInitials}</span>
            <span className="proof-nav-tray__rail-tip" role="tooltip" aria-hidden="true">
              {firstName}
            </span>
          </button>

          <span className="proof-nav-tray__rail-sep" aria-hidden="true" />

          <RailButton
            label="Library"
            icon={<IconLibrary />}
            active={resolvedActive === "library"}
            onClick={() => handleRow(onLibrary)}
            testId="nav-tray-rail-library"
          />
          <RailButton
            label="Feed"
            icon={<IconFeed />}
            active={resolvedActive === "feed"}
            onClick={() => handleRow(onFeed)}
            testId="nav-tray-rail-feed"
          />
          <RailButton
            label="Discover"
            icon={<IconDiscover />}
            active={resolvedActive === "discover"}
            onClick={() => handleRow(onDiscover)}
            testId="nav-tray-rail-discover"
          />
          <RailButton
            label="Cooks"
            icon={<IconCooks />}
            active={resolvedActive === "cooks"}
            onClick={() => handleRow(onCooks)}
            testId="nav-tray-rail-cooks"
          />
          <RailButton
            label="Groups"
            icon={<IconGroups />}
            active={resolvedActive === "groups"}
            onClick={() => handleRow(onGroups)}
            testId="nav-tray-rail-groups"
          />
          <RailButton
            label="Friends"
            icon={<IconFriends />}
            onClick={() => handleRow(onFriends)}
            testId="nav-tray-rail-friends"
          />

          <button
            type="button"
            className="proof-nav-tray__rail-btn proof-nav-tray__rail-expand"
            onClick={handleExpand}
            data-testid="nav-tray-rail-expand"
            aria-label="Open navigation"
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <span className="proof-nav-tray__rail-icon" aria-hidden="true">
              <IconExpand />
            </span>
            <span className="proof-nav-tray__rail-tip" role="tooltip" aria-hidden="true">
              Expand
            </span>
          </button>
        </div>
      )}

      {/* Scrim — mobile always; desktop only in rail-overlay mode (CSS-hidden on
          the docked browse column). Dismisses on tap. */}
      <div
        className="proof-nav-tray__scrim"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
        data-testid="nav-tray-scrim"
      />

      <nav className="proof-nav-tray__panel" aria-label="Primary">
        {/* Identity header */}
        <header className="proof-nav-tray__identity">
          <span className="proof-nav-tray__avatar" aria-hidden="true">
            {avatarInitials}
          </span>
          <span className="proof-nav-tray__identity-text">
            <span className="proof-nav-tray__identity-name">{firstName}</span>
            <span className="proof-nav-tray__identity-email">{email}</span>
          </span>
          {/* Dismiss — mobile only (CSS-hidden ≥1024px). */}
          <button
            type="button"
            className="proof-nav-tray__close"
            onClick={() => onOpenChange(false)}
            aria-label="Close menu"
            data-testid="nav-tray-close"
          >
            <IconClose />
          </button>
        </header>

        {/* Scrollable sections */}
        <div className="proof-nav-tray__scroll" ref={scrollRef} onScroll={syncScrollCue}>
          <section className="proof-nav-tray__section" aria-labelledby="nav-tray-label-cook">
            <h2 className="proof-nav-tray__section-label" id="nav-tray-label-cook">
              Cook
            </h2>
            <Row
              label="Library"
              icon={<IconLibrary />}
              active={resolvedActive === "library"}
              onClick={() => handleRow(onLibrary)}
              testId="nav-tray-library"
            />
            <Row
              label="Feed"
              icon={<IconFeed />}
              active={resolvedActive === "feed"}
              onClick={() => handleRow(onFeed)}
              testId="user-menu-feed"
            />
            <Row
              label="Discover"
              icon={<IconDiscover />}
              active={resolvedActive === "discover"}
              onClick={() => handleRow(onDiscover)}
              testId="nav-tray-discover"
            />
            <Row
              label="Cooks"
              icon={<IconCooks />}
              active={resolvedActive === "cooks"}
              onClick={() => handleRow(onCooks)}
              testId="user-menu-cooks"
            />
            <Row
              label="Groups"
              icon={<IconGroups />}
              active={resolvedActive === "groups"}
              onClick={() => handleRow(onGroups)}
              testId="user-menu-groups"
            />
            <Row
              label="Friends"
              icon={<IconFriends />}
              onClick={() => handleRow(onFriends)}
              testId="nav-tray-friends"
            />
          </section>

          <section className="proof-nav-tray__section" aria-labelledby="nav-tray-label-account">
            <h2 className="proof-nav-tray__section-label" id="nav-tray-label-account">
              Account
            </h2>
            <Row
              label="Profile"
              icon={<IconPerson />}
              active={resolvedActive === "profile"}
              onClick={() => handleRow(onProfile)}
              testId="user-menu-profile"
            />
            <Row
              label="Settings"
              icon={<IconGear />}
              onClick={() => handleRow(onSettings)}
              testId="user-menu-settings"
            />
            <Row
              label="Export"
              icon={<IconExport />}
              onClick={() => handleRow(onExports)}
              testId="user-menu-exports"
            />
            <Row
              label="Custom logic"
              icon={<IconCode />}
              active={resolvedActive === "custom-logic"}
              onClick={() => handleRow(onCustomLogic)}
              testId="user-menu-custom-logic"
            />
            <Row
              label="Syntax help"
              icon={<IconBook />}
              active={resolvedActive === "help"}
              onClick={() => handleRow(onHelp)}
              testId="user-menu-help"
            />
            <Row
              label="Send feedback"
              icon={<IconFeedback />}
              onClick={() => handleRow(onFeedback)}
              testId="user-menu-feedback"
            />
            {isAdmin && onAdmin !== undefined && (
              <Row
                label="Admin"
                icon={<IconAdmin />}
                onClick={() => handleRow(onAdmin)}
                testId="user-menu-admin"
              />
            )}
          </section>
        </div>

        {/* Bottom scroll cue. At 375x667 the tail of the Account section
            ("Syntax help", "Send feedback") sits below the fold, and a top-level
            feature with no signal that it exists is a §6.0a discoverability
            failure. Zero-height in flow (negative margin), so it overlays the
            tail of the list without moving anything. */}
        <div
          className="proof-nav-tray__scroll-fade"
          data-more={hasMoreBelow ? "true" : "false"}
          data-testid="nav-tray-scroll-fade"
          aria-hidden="true"
        />

        {/* Sign out — pinned to the bottom (Rule 8: the caller's handler surfaces
            its own failure). */}
        <div className="proof-nav-tray__footer">
          <Row
            label="Sign out"
            icon={<IconSignOut />}
            onClick={() => handleRow(onSignOut)}
            testId="user-menu-sign-out"
            destructive
          />
        </div>
      </nav>
    </aside>
  );
}
